/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, ISourceConversationProblem } from '@datatype/iyagigo/api-type';

import { useTypedSelector } from '@hook/common/useStore';
import Gap from '@component/common/gap';
import { v4 as uuid4 } from 'uuid';
import Scroll, { ScrollContainer as ChoicesScrollContainer } from '@component/common/scroll';

interface IProps {
  sourceProblem: ISourceConversationProblem;
  selectedChoiceId: string | null;
  onChoiceSelect: (choiceId: string) => void;
  onChoiceSubmit: () => void;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100%;
  width: 100%;
`;

const ChoicesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: 5px;
`;

const ChoiceContainer = styled.div<{ selected: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  padding: 5px;
  border-radius: ${size.BORDER_RADIUS_1};
  border: ${size.BORDER_WIDTH_1} solid ${(props) => (props.selected ? color.BROWN_2 : color.GRAY_4)};
  background-color: transparent;
  cursor: pointer;
  width: 100%;
`;

const QUESTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', width: '100%' },
} as const;

const CHOICE_LABEL_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', width: '25px', event: { cursor: 'pointer' } },
} as const;

const CHOICE_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left', width: 'fill', event: { cursor: 'pointer' } },
} as const;

const ProblemControl: React.FC<IProps> = ({ sourceProblem, selectedChoiceId, onChoiceSelect, onChoiceSubmit }) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;

  const Choices = sourceProblem.choice.map((choice, idx) => {
    const choiceId = String(idx);
    const choiceLabel = `${String.fromCharCode(65 + idx)})`;
    return (
      <ChoiceContainer key={uuid4()} selected={selectedChoiceId === choiceId} onClick={() => onChoiceSelect(choiceId)}>
        <TextBlock text={choiceLabel} design={CHOICE_LABEL_DESIGN} />
        <TextBlock
          text={choice.sentence}
          annotation={isJapanese && choice.pronunciation ? { reading: choice.pronunciation } : undefined}
          design={CHOICE_TEXT_DESIGN}
        />
      </ChoiceContainer>
    );
  });

  return (
    <Wrapper>
      <TextBlock text={'어떻게 이야기할까요?'} design={QUESTION_DESIGN} />
      <Gap height={'5px'} />
      <ChoicesScrollContainer width={'100%'}>
        <Scroll height={'100%'} direction={'y'} showScrollIndicator={true}>
          <ChoicesContainer>{Choices}</ChoicesContainer>
        </Scroll>
      </ChoicesScrollContainer>
      <Gap height={size.MARGIN_1} />
      <TextButton
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width={'100%'}
        text={'정답확인'}
        disabled={!selectedChoiceId}
        handleClick={onChoiceSubmit}
      />
    </Wrapper>
  );
};

export default ProblemControl;
